import {
  IMAGE_COMPRESSION_MAX_DIMENSION,
  IMAGE_COMPRESSION_TARGET_SIZE,
  IMAGE_COMPRESSION_THRESHOLD
} from "lib/constants";

interface CompressImageOptions {
  maxDimension?: number;
}

const COMPRESSIBLE_TYPES = ["image/jpeg", "image/png"];

// JPEG quality bounds for binary search. The encoder's size response is
// highly non-linear near q=1.0 (one step of 0.05 can halve file size), so a
// fixed ladder overshoots wildly. Binary search finds the highest quality
// that still fits under the target.
//
// Canvas JPEG quality is internally quantized — consecutive q values often
// produce identical output. We cap iterations + detect size plateaus to
// avoid wasting time on steps that can't improve the result.
const JPEG_MIN_QUALITY = 0.8;
const JPEG_QUALITY_SEARCH_TOLERANCE = 0.02;
const JPEG_QUALITY_SEARCH_MAX_ITERATIONS = 4;
const JPEG_PLATEAU_SIZE_RATIO = 0.01; // treat two sizes within 1% as identical

// Dimension ceiling for attempting full-resolution encode. Browsers cap
// canvas at ~16384px per side; we stay well under that. Above this we fall
// back to the maxDimension-capped scale. Pixel count at 10000² is ~100MP /
// ~400MB canvas buffer — acceptable for desktop image editing.
const MAX_SAFE_DIMENSION_FOR_FULL_SCALE = 10000;

export const isCompressibleImage = (file: File): boolean => {
  return COMPRESSIBLE_TYPES.includes(file.type.toLowerCase());
};

export const getCompressibleFiles = (
  files: File[],
  threshold: number = IMAGE_COMPRESSION_THRESHOLD
): File[] => {
  return files.filter(
    file => isCompressibleImage(file) && file.size > threshold
  );
};

const loadImageFromFile = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Failed to load image: ${file.name}`));
    };
    img.src = url;
  });
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Canvas toBlob returned null"));
        }
      },
      type,
      quality
    );
  });
};

const getOutputFilename = (
  originalName: string,
  outputType: string
): string => {
  const lastDot = originalName.lastIndexOf(".");
  const baseName =
    lastDot !== -1 ? originalName.substring(0, lastDot) : originalName;

  if (outputType === "image/jpeg") {
    return `${baseName}.jpg`;
  }
  if (outputType === "image/png") {
    return `${baseName}.png`;
  }
  return originalName;
};

const isPng = (file: File): boolean => file.type.toLowerCase() === "image/png";

const getOutputType = (file: File): string =>
  isPng(file) ? "image/png" : "image/jpeg";

const buildScaleLadder = (
  naturalWidth: number,
  naturalHeight: number,
  maxDimension: number
): number[] => {
  const naturalMax = Math.max(naturalWidth, naturalHeight);
  const cappedScale = Math.min(1, maxDimension / naturalMax);

  const steps: number[] = [];

  // Try original dimensions first so lightly-oversized files get a light
  // touch — re-encoding alone is often enough to drop under the target.
  // Skip only if the source is absurdly large and would OOM the canvas.
  if (naturalMax <= MAX_SAFE_DIMENSION_FOR_FULL_SCALE) {
    steps.push(1);
  }

  // Then fall back to the maxDimension cap, then progressively smaller.
  steps.push(cappedScale, cappedScale * 0.85, cappedScale * 0.7);

  return Array.from(new Set(steps))
    .filter(s => s > 0)
    .sort((a, b) => b - a);
};

export const compressImage = async (
  file: File,
  options?: CompressImageOptions
): Promise<File> => {
  if (!isCompressibleImage(file)) {
    return file;
  }

  const maxDimension = options?.maxDimension ?? IMAGE_COMPRESSION_MAX_DIMENSION;
  const targetSize = IMAGE_COMPRESSION_TARGET_SIZE;

  const img = await loadImageFromFile(file);
  const outputType = getOutputType(file);

  const scaleLadder = buildScaleLadder(
    img.naturalWidth,
    img.naturalHeight,
    maxDimension
  );

  let bestBlob: Blob | null = null;
  let done = false;

  const tryEncode = async (
    canvas: HTMLCanvasElement,
    quality: number | undefined
  ): Promise<Blob | null> => {
    try {
      return await canvasToBlob(canvas, outputType, quality ?? 1);
    } catch {
      return null;
    }
  };

  // Track the smallest attempted blob across all scales as a best-effort
  // fallback for when no configuration fits under the target. Declared at the
  // function scope (not inside the loop) so ESLint's no-loop-func is happy
  // about the closure capturing `bestBlob`.
  const trackFallback = (blob: Blob) => {
    if (!bestBlob || blob.size < bestBlob.size) {
      bestBlob = blob;
    }
  };

  for (const scale of scaleLadder) {
    if (done) break;

    const width = Math.max(1, Math.round(img.naturalWidth * scale));
    const height = Math.max(1, Math.round(img.naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      canvas.width = 0;
      canvas.height = 0;
      continue;
    }
    ctx.drawImage(img, 0, 0, width, height);

    if (outputType === "image/png") {
      // PNG toBlob ignores quality; single attempt per scale.
      const blob = await tryEncode(canvas, undefined);
      if (blob) {
        if (blob.size <= targetSize) {
          bestBlob = blob;
          done = true;
        } else {
          trackFallback(blob);
        }
      }
    } else {
      // JPEG: binary search highest quality that fits the target.
      const highBlob = await tryEncode(canvas, 1);
      if (highBlob) {
        if (highBlob.size <= targetSize) {
          // q=1.0 already fits — best possible at this scale.
          bestBlob = highBlob;
          done = true;
        } else {
          trackFallback(highBlob);

          // Check the low bound; if even that overshoots, try next scale.
          const lowBlob = await tryEncode(canvas, JPEG_MIN_QUALITY);
          if (lowBlob) {
            if (lowBlob.size > targetSize) {
              trackFallback(lowBlob);
            } else {
              // Binary search between JPEG_MIN_QUALITY and 1 for highest fit.
              let qLo = JPEG_MIN_QUALITY;
              let qHi = 1;
              let fitBlob: Blob = lowBlob;
              // Size at the upper bound (over target). Plateau detection:
              // if a midpoint produces the same size as this, every q above
              // it yields identical output — stop searching higher.
              const hiSize = highBlob.size;

              for (
                let i = 0;
                i < JPEG_QUALITY_SEARCH_MAX_ITERATIONS &&
                qHi - qLo > JPEG_QUALITY_SEARCH_TOLERANCE;
                i++
              ) {
                const qMid = (qLo + qHi) / 2;
                const midBlob = await tryEncode(canvas, qMid);
                if (!midBlob) break;
                const onPlateau =
                  Math.abs(midBlob.size - hiSize) <=
                  hiSize * JPEG_PLATEAU_SIZE_RATIO;
                if (midBlob.size <= targetSize) {
                  qLo = qMid;
                  fitBlob = midBlob;
                  if (onPlateau) break;
                } else {
                  qHi = qMid;
                  if (onPlateau) break;
                }
              }

              bestBlob = fitBlob;
              done = true;
            }
          }
        }
      }
    }

    // Release the canvas pixel buffer before moving to the next scale step
    canvas.width = 0;
    canvas.height = 0;
  }

  // Only use the compressed version if it's actually smaller than the original
  if (!bestBlob || bestBlob.size >= file.size) {
    return file;
  }

  const outputFilename = getOutputFilename(file.name, outputType);

  return new File([bestBlob], outputFilename, {
    type: outputType,
    lastModified: file.lastModified
  });
};

interface CompressFilesResult {
  files: File[];
  compressedCount: number;
}

export const compressFilesForUpload = async (
  files: File[],
  threshold: number = IMAGE_COMPRESSION_THRESHOLD
): Promise<CompressFilesResult> => {
  const compressibleFiles = getCompressibleFiles(files, threshold);
  if (compressibleFiles.length === 0) {
    return { files, compressedCount: 0 };
  }

  const compressedMap = new Map<File, File>();
  let compressedCount = 0;
  for (const file of compressibleFiles) {
    try {
      const compressed = await compressImage(file);
      compressedMap.set(file, compressed);
      if (compressed !== file) {
        compressedCount++;
      }
    } catch (e) {
      // Fall back to original on compression failure
      compressedMap.set(file, file);
    }
  }

  return {
    files: files.map(f => compressedMap.get(f) || f),
    compressedCount
  };
};
